import {currentState, type HistoryState, updateCurrentState} from '@github-ui/history'
import {STAFF_NAVIGATION_PARAMS} from '@github-ui/react-app-navigation/staff-navigation-params'
import {softNavigate} from '@github-ui/soft-navigate'

import type {GlobalNavigationContext} from '../global-navigation'

export const GLOBAL_NAV_CONTEXT_REFRESH_DESCRIPTOR_STATE = 'globalNavContextRefreshDescriptor'

export type GlobalNavContextRefreshDescriptor = GlobalNavigationContext

export function getCurrentLocationPath() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

export function getCurrentSelectedLink(selectedLink?: string): string | undefined {
  if (selectedLink) return selectedLink
  if (typeof document === 'undefined') return undefined

  return document.head?.querySelector<HTMLMetaElement>('meta[name="selected-link"]')?.getAttribute('value') ?? undefined
}

export function locationMatchesGlobalNavContext(
  path: string,
  {ignoreHash = false}: {ignoreHash?: boolean} = {},
): boolean {
  let expected: URL
  try {
    expected = new URL(path, window.location.origin)
  } catch {
    return false
  }

  const current = new URL(window.location.href, window.location.origin)
  if (
    current.origin !== expected.origin ||
    current.pathname !== expected.pathname ||
    (!ignoreHash && current.hash !== expected.hash)
  ) {
    return false
  }

  for (const param of STAFF_NAVIGATION_PARAMS) {
    current.searchParams.delete(param)
    expected.searchParams.delete(param)
  }
  current.searchParams.sort()
  expected.searchParams.sort()
  return current.search === expected.search
}

/** Reloads the current page through Turbo when the browser is still at `path`, so its server-rendered context is current. */
export function reloadIfAtLocation(path: string): boolean {
  if (!locationMatchesGlobalNavContext(path)) return false
  softNavigate(getCurrentLocationPath())
  return true
}

/** Resolves with the error a navigation rejected with, or `undefined` once it succeeds. */
export async function getNavigationError(navigationCompletion?: Promise<void>): Promise<unknown> {
  try {
    await navigationCompletion
    return undefined
  } catch (error) {
    return error
  }
}

/** Stores `descriptor` on the current history entry, or clears it when `undefined`. */
export function setCurrentGlobalNavContext(descriptor: GlobalNavContextRefreshDescriptor | undefined) {
  const state: HistoryState & {[GLOBAL_NAV_CONTEXT_REFRESH_DESCRIPTOR_STATE]?: GlobalNavContextRefreshDescriptor} = {
    [GLOBAL_NAV_CONTEXT_REFRESH_DESCRIPTOR_STATE]: descriptor,
  }
  updateCurrentState(state)
}

/** Reads a descriptor from a history state, including React Router's user state (`usr`). */
export function getGlobalNavContextRefreshDescriptor(
  state: unknown = currentState(),
): GlobalNavContextRefreshDescriptor | undefined {
  if (!isRecord(state)) return undefined

  const descriptor =
    state[GLOBAL_NAV_CONTEXT_REFRESH_DESCRIPTOR_STATE] ??
    (isRecord(state.usr) ? state.usr[GLOBAL_NAV_CONTEXT_REFRESH_DESCRIPTOR_STATE] : undefined)
  const isDescriptor =
    isRecord(descriptor) &&
    typeof descriptor.path === 'string' &&
    typeof descriptor.crumbType === 'string' &&
    typeof descriptor.crumbObjectIdentifier === 'string' &&
    typeof descriptor.selectedLink === 'string' &&
    (descriptor.scope === undefined || typeof descriptor.scope === 'string')
  return isDescriptor ? (descriptor as unknown as GlobalNavContextRefreshDescriptor) : undefined
}

export function isLocationInScope(scope: string): boolean {
  const pathname = window.location.pathname.toLowerCase()
  const scopePath = scope.toLowerCase().replace(/\/+$/, '')
  return pathname === scopePath || pathname.startsWith(`${scopePath}/`)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}
